// Pieces the schedule board, the capacity view and the workbench share (spec
// 03 §4.1, §4.6, §4.7; P3-I-2): the run chips, a run's error and outcome in
// words, an operation's order label for its reader (§7 narrowing (a)) and the
// Solve control with its poll.

import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { StatusChip, type ChipVariant } from "../../../../components/status-chip";
import { Banner } from "../../../../components/toast";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import {
  requestSolve,
  useScheduleRefresh,
  useScheduleRun,
  type ScheduleOperation,
  type ScheduleOrderLabel,
  type ScheduleRun,
  type ScheduleRunStatus,
  type ScheduleRunTrigger,
  type ScheduleUnplacedReason,
} from "../hooks/schedule-queries";
import { useOffline } from "../lib/planning-lib";
import { runOutcome } from "../lib/schedule-lib";

const STATUS_CHIP: Readonly<Record<ScheduleRunStatus, ChipVariant>> = {
  running: "accent",
  succeeded: "ok",
  failed: "bad",
};

export function SolveStatusChip({
  status,
  testId,
}: {
  status: ScheduleRunStatus;
  testId?: string;
}) {
  const { t } = useTranslation();
  return (
    <StatusChip
      variant={STATUS_CHIP[status]}
      {...(testId !== undefined ? { "data-testid": testId } : {})}
      data-status={status}
    >
      {t(`planning.schedule.runStatus.${status}`)}
    </StatusChip>
  );
}

export function SolveTriggerChip({ trigger }: { trigger: ScheduleRunTrigger }) {
  const { t } = useTranslation();
  return (
    <StatusChip variant="neutral" dot={false} data-status={trigger}>
      {t(`planning.schedule.trigger.${trigger}`)}
    </StatusChip>
  );
}

/** Why an operation is not placed, in words (§4.6, D-397 ruling 7). */
export function UnplacedReasonText({ reason }: { reason: ScheduleUnplacedReason | null }) {
  const { t } = useTranslation();
  if (reason === null) return null;
  return (
    <span
      className="planning-warn"
      data-testid="planning-schedule-unplaced-reason"
      data-reason={reason}
    >
      {t(`planning.schedule.unplaced.${reason}`)}
    </span>
  );
}

/** A failed run's error key in words. */
export function SolveErrorLine({ run }: { run: Pick<ScheduleRun, "error"> }) {
  const { t } = useTranslation();
  if (run.error === null) return null;
  return (
    <span className="planning-bad" data-testid="planning-solve-error" data-error={run.error}>
      {t(`planning.schedule.runError.${run.error}`)}
    </span>
  );
}

/**
 * The order an operation belongs to as its reader may see it (§7 narrowing
 * (a)): the number, linked when the order's own door opens it, else the kind
 * in words. A work order links to module 04's detail and a planned order to
 * the planned-orders drawer.
 */
export function ScheduleOrderText({
  op,
  testId,
}: {
  op: Pick<ScheduleOperation, "source_kind" | "work_order_id" | "planned_order_id"> & {
    readonly label: ScheduleOrderLabel;
  };
  testId?: string;
}) {
  const { t } = useTranslation();
  const label = op.label;
  const text =
    label.order_no ??
    t(
      op.source_kind === "wo_operation"
        ? "planning.schedule.block.workOrder"
        : "planning.schedule.block.plannedOrder",
    );
  const stop = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
  };
  if (label.order_link && op.work_order_id !== null) {
    return (
      <Link
        to="/production/work-orders/$id"
        params={{ id: op.work_order_id }}
        data-testid={testId}
        data-linked="true"
        onClick={stop}
      >
        {text}
      </Link>
    );
  }
  if (label.order_link && op.planned_order_id !== null) {
    return (
      <Link
        to="/planning/orders"
        search={{ order: op.planned_order_id }}
        data-testid={testId}
        data-linked="true"
        onClick={stop}
      >
        {text}
      </Link>
    );
  }
  return (
    <span data-testid={testId} data-linked="false">
      {text}
    </span>
  );
}

/** The sales line an order is pegged to, as module 09 labels it for the reader. */
export function SalesLineText({ label }: { label: ScheduleOrderLabel }) {
  const { t } = useTranslation();
  const sales = label.sales_order;
  if (sales === null) return null;
  const words =
    sales.order_no !== null
      ? [
          sales.line_no !== null ? `${sales.order_no} / ${String(sales.line_no)}` : sales.order_no,
          sales.customer_name,
        ]
          .filter((v) => v !== null && v.length > 0)
          .join(" · ")
      : t("planning.schedule.drawer.salesOrder");
  if (sales.order_id !== null) {
    return (
      <Link
        to="/sales/orders/$orderId"
        params={{ orderId: sales.order_id }}
        data-testid="planning-schedule-sales-line"
      >
        {words}
      </Link>
    );
  }
  return <span data-testid="planning-schedule-sales-line">{words}</span>;
}

export interface SolveControl {
  readonly running: boolean;
  readonly start: () => void;
  readonly button: ReactNode;
  readonly banner: ReactNode;
}

/**
 * Solve (§4.6, §5 row 56): asks for a solve, follows the run it started or
 * joined every 5 seconds until it ends, re-reads every schedule read of the
 * facility, and says how it ended. A person's solve always leaves its draft
 * waiting for a person (§3.5).
 */
export function useSolveControl(
  facilityId: string,
  options: { canSolve: boolean; testIdPrefix?: string },
): SolveControl {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const offline = useOffline();
  const refresh = useScheduleRefresh();
  const prefix = options.testIdPrefix ?? "planning-solve";
  const [watching, setWatching] = useState<string | undefined>(undefined);
  const [queued, setQueued] = useState(false);
  const [starting, setStarting] = useState(false);
  const [failure, setFailure] = useState<unknown>(undefined);
  const [outcome, setOutcome] = useState<ScheduleRun | undefined>(undefined);
  const run = useScheduleRun(watching);

  useEffect(() => {
    setWatching(undefined);
    setOutcome(undefined);
    setFailure(undefined);
    setQueued(false);
  }, [facilityId]);

  const done = run.data !== undefined && run.data.status !== "running" ? run.data : undefined;
  useEffect(() => {
    if (done === undefined) return;
    setOutcome(done);
    setWatching(undefined);
    void refresh(facilityId);
  }, [done, facilityId, refresh]);

  const start = async () => {
    setStarting(true);
    setFailure(undefined);
    setOutcome(undefined);
    setQueued(false);
    try {
      const accepted = await requestSolve(facilityId);
      setQueued(accepted.queued);
      setWatching(accepted.schedule_run_id);
      void refresh(facilityId);
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      setFailure(error);
    } finally {
      setStarting(false);
    }
  };

  const running = starting || watching !== undefined;
  const button = options.canSolve ? (
    <Button
      variant="primary"
      busy={running}
      busyLabel={t("planning.schedule.solve.running")}
      disabled={offline}
      data-testid={prefix}
      onClick={() => void start()}
    >
      {t("planning.schedule.solve.button")}
    </Button>
  ) : null;

  let banner: ReactNode = null;
  if (failure !== undefined) {
    banner = (
      <Banner kind="bad" data-testid={`${prefix}-failure`}>
        {t("planning.schedule.solve.requestFailed", { requestId: requestIdOf(failure) ?? "-" })}
      </Banner>
    );
  } else if (watching !== undefined && queued) {
    banner = (
      <p className="planning-note" role="status" data-testid={`${prefix}-queued`}>
        {t("planning.schedule.solve.queued")}
      </p>
    );
  } else if (outcome !== undefined) {
    const kind = runOutcome(outcome);
    banner =
      kind === "failed" ? (
        <Banner kind="bad" data-testid={`${prefix}-done`} data-outcome={kind}>
          {t("planning.schedule.solve.outcome.failed")} <SolveErrorLine run={outcome} />
        </Banner>
      ) : (
        <Banner
          kind={kind === "stale_base" ? "warn" : "ok"}
          data-testid={`${prefix}-done`}
          data-outcome={kind}
        >
          {t(`planning.schedule.solve.outcome.${kind}`)}
        </Banner>
      );
  } else if (offline && options.canSolve) {
    banner = <p className="planning-note">{t("planning.common.offlineWrites")}</p>;
  }
  return { running, start: () => void start(), button, banner };
}
